import { Dialog } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { showErrorNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import React, { FC, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { graphql } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { validateRequired } from 'src/shared/validation';
import { InputWithValidation } from '../../../shared/globals';
import { renameSalesChannelDialogMutation } from './__generated__/renameSalesChannelDialogMutation.graphql';
import { renameSalesChannelDialogQuery } from './__generated__/renameSalesChannelDialogQuery.graphql';

interface RenameSalesChannelDialogProps {
    id: string;
    onClosed: () => void;
}

const updateNameSalesChannel = graphql`
    mutation renameSalesChannelDialogMutation($input: SalesChannelDetailInput!) {
        upsertSalesChannelDetail(input: $input) {
            id
            properties {
                name
            }
        }
    }
`;

const RenameSalesChannelDialog: FC<RenameSalesChannelDialogProps> = (salesChannel) => {
    const query = useLazyLoadQuery<renameSalesChannelDialogQuery>(
        graphql`
            query renameSalesChannelDialogQuery($id: ID!) {
                ecommerceSettings {
                    ecommerceName
                    remoteUrl
                }
                salesChannel(id: $id) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                }
                subscription {
                    areSalesChannelsEnabled
                }
            }
        `,
        { id: salesChannel.id }
    );

    const [name, setName] = useState<string>(query.salesChannel?.detail?.properties?.name || '');
    const [updateNameSalesChannelMutation, isInFightSetStatus] = useMutation<renameSalesChannelDialogMutation>(updateNameSalesChannel);

    const properties = { name: name };

    const handleConfirm = () => {
        if (name === '') {
            showErrorNotification({ message: T._('Name is required', 'Admin') });
            return;
        }

        updateNameSalesChannelMutation({
            variables: {
                input: {
                    id: query.salesChannel?.detail?.id,
                    salesChannelGlobalID: salesChannel.id,
                    properties: properties
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed rename sales channel', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Sales channel renamed', 'Admin') });
                salesChannel.onClosed();
            }
        });
    };

    return (
        <Dialog
            title={T._('Rename sales channel', 'Admin')}
            buttons={[
                {
                    label: T._('Confirm', 'Admin'),
                    onClick: () => {
                        handleConfirm();
                    }
                }
            ]}
        >
            <FormControl label={T._('Name', 'Admin')}>
                <InputWithValidation
                    value={name || ''}
                    onChange={(e) => setName(e.currentTarget.value)}
                    validationKey={`salesChannelName`}
                    validate={(value) => validateRequired(value)}
                    message={T._('Required', 'Admin')}
                    autoFocus
                />
            </FormControl>
        </Dialog>
    );
};

export default RenameSalesChannelDialog;
